/**
 * useVerifiedAccess Hook
 * 
 * Hook per gestire l'accesso verificato tramite Magic Link.
 * 
 * DISTINZIONE:
 * - Consultazione: Accesso libero con Travel Pass code
 * - Azioni sensibili: Richiedono verifica via Magic Link
 * 
 * FASE ATTUALE: Solo gate, no azioni operative.
 * 
 * Workflow:
 * 1. L'utente accede alla pagina (consultazione)
 * 2. Per azioni sensibili, clicca "Verifica accesso"
 * 3. Inserisce email, riceve Magic Link
 * 4. Clicca il link, ottiene sessione verificata
 * 5. Può accedere alle azioni sensibili per 2 ore
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Session storage key
const SESSION_TOKEN_KEY = 'fu_verified_session';

/**
 * Access levels
 */
export const ACCESS_LEVELS = {
  CONSULTATIVE: 'consultative',
  VERIFIED: 'verified'
};

/**
 * Hook per gestire l'accesso verificato
 * @param {string} code - Codice del journey/travel pass
 * @returns {Object} Stato accesso e funzioni
 */
export function useVerifiedAccess(code) {
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestingLink, setRequestingLink] = useState(false);
  const [linkRequested, setLinkRequested] = useState(false);

  // Get stored session token
  const getStoredSessionToken = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(`${SESSION_TOKEN_KEY}_${code}`);
      return stored || null;
    } catch {
      return null;
    }
  }, [code]);

  // Store session token
  const storeSessionToken = useCallback((token) => {
    try {
      sessionStorage.setItem(`${SESSION_TOKEN_KEY}_${code}`, token);
    } catch (err) {
      console.error('Failed to store session token:', err);
    }
  }, [code]);

  // Clear session token
  const clearSessionToken = useCallback(() => {
    try {
      sessionStorage.removeItem(`${SESSION_TOKEN_KEY}_${code}`);
    } catch (err) {
      console.error('Failed to clear session token:', err);
    }
  }, [code]);

  // Fetch access status
  const fetchAccessStatus = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const sessionToken = getStoredSessionToken();
      let url = `${API_URL}/api/journey/${code}/access-status`;
      
      if (sessionToken) {
        url += `?session_token=${encodeURIComponent(sessionToken)}`;
      }

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch access status: ${response.status}`);
      }

      const data = await response.json();
      setAccessStatus(data);

      // If session is invalid, clear token
      if (sessionToken && !data.verified) {
        clearSessionToken();
      }

    } catch (err) {
      console.error('Error fetching access status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [code, getStoredSessionToken, clearSessionToken]);

  // Initial fetch
  useEffect(() => {
    fetchAccessStatus();
  }, [fetchAccessStatus]);

  // Check URL for verification callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const sessionToken = urlParams.get('session_token');

    if (verified === 'true' && sessionToken) {
      storeSessionToken(sessionToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh status
      fetchAccessStatus();
    }
  }, [storeSessionToken, fetchAccessStatus]);

  // Request magic link
  const requestMagicLink = useCallback(async (email) => {
    if (!code || !email) {
      return { success: false, message: 'Email richiesta' };
    }

    try {
      setRequestingLink(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_code: code,
          email: email.trim(),
          purpose: 'sensitive_actions_access'
        })
      });

      const data = await response.json();
      
      // Handle rate limiting
      if (data.rate_limited) {
        const minutes = Math.ceil((data.retry_after_seconds || 300) / 60);
        setError(`Attendi ${minutes} minuti prima di riprovare`);
        return { 
          success: false, 
          rate_limited: true,
          message: data.message,
          retry_after_seconds: data.retry_after_seconds
        };
      }
      
      if (data.success) {
        setLinkRequested(true);
        setError(null);
      }

      return data;

    } catch (err) {
      console.error('Error requesting magic link:', err);
      setError('Errore di connessione. Riprova più tardi.');
      return { success: false, message: 'Errore di rete' };
    } finally {
      setRequestingLink(false);
    }
  }, [code]);

  // Verify magic link (called from verification page)
  const verifyMagicLink = useCallback(async (token, purpose, signature) => {
    if (!code || !token) {
      return { success: false, message: 'Token non valido' };
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/magic-link/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journey_code: code,
          token,
          purpose,
          signature
        })
      });

      const data = await response.json();

      if (data.success && data.session_token) {
        storeSessionToken(data.session_token);
        await fetchAccessStatus();
      }

      return data;

    } catch (err) {
      console.error('Error verifying magic link:', err);
      return { success: false, message: 'Errore di verifica' };
    }
  }, [code, storeSessionToken, fetchAccessStatus]);

  // Logout / clear verified session
  const clearVerifiedSession = useCallback(() => {
    clearSessionToken();
    setAccessStatus(prev => ({
      ...prev,
      verified: false,
      access_level: ACCESS_LEVELS.CONSULTATIVE,
      sensitive_actions_available: false,
      session: null
    }));
  }, [clearSessionToken]);

  // Computed values
  const isVerified = useMemo(() => {
    return accessStatus?.verified === true;
  }, [accessStatus]);

  const accessLevel = useMemo(() => {
    return accessStatus?.access_level || ACCESS_LEVELS.CONSULTATIVE;
  }, [accessStatus]);

  const canAccessSensitiveActions = useMemo(() => {
    return accessStatus?.sensitive_actions_available === true;
  }, [accessStatus]);

  const sessionMinutesRemaining = useMemo(() => {
    return accessStatus?.session?.minutes_remaining || 0;
  }, [accessStatus]);

  const emailHint = useMemo(() => {
    return accessStatus?.email_hint || '';
  }, [accessStatus]);

  // Get current session token (for API calls)
  const sessionToken = useMemo(() => {
    return getStoredSessionToken();
  }, [getStoredSessionToken, accessStatus]); // Re-compute when access status changes

  return {
    // Status
    accessStatus,
    loading,
    error,
    
    // Computed
    isVerified,
    accessLevel,
    canAccessSensitiveActions,
    sessionMinutesRemaining,
    emailHint,
    sessionToken, // Expose session token for API calls
    
    // Request state
    requestingLink,
    linkRequested,
    
    // Actions
    requestMagicLink,
    verifyMagicLink,
    clearVerifiedSession,
    refreshStatus: fetchAccessStatus
  };
}

export default useVerifiedAccess;
